/**
 * The receipt tape's data shape, shared so that the ONE renderer in
 * `@mlm/ui` can be fed from more than one app.
 *
 * These types used to live in `apps/cashier/src/lib/api.ts` (and `ReceiptHeader`
 * was separately re-declared in `apps/admin/src/lib/api.ts`, and `PosReceiptItem`
 * again in `apps/api/src/services/pos.ts`). They moved here when Admin → Reports
 * grew a receipt drill-down: two apps rendering the same customer document from
 * two copies of the shape is exactly how a reprint starts disagreeing with the
 * original tape.
 */

import type { ReceiptFieldFlags } from './receipt-fields';

/** One sold line as the tape prints it. */
export type PosReceiptItem = {
  name: string;
  variantName: string | null;
  qty: number;
  unitPrice: string;
  lineTotal: string;
};

/** The company block at the top of the tape (`branches.receipt_header`). */
export type ReceiptHeader = {
  companyName?: string;
  address?: string;
  tin?: string;
  phone?: string;
  footer?: string;
};

/**
 * BF3 — normalized receipt shape rendered by the checkout flow, the releasing
 * queue (release + reprint) and the admin sales drill-down. The `.receipt-tape`
 * element is the only thing that survives `@media print`, sized for ~80mm
 * thermal.
 */
export type ReceiptData = {
  orderCode: string;
  status: string;
  /** Sold lines. Always printed — a ₱0 item still gets its name on the tape. */
  items: PosReceiptItem[];
  subtotal: string;
  discountTotal: string;
  total: string;
  /** `method` is the coarse tender bucket; `methodLabel` is what the cashier
   *  actually picked ("BPI Bank"). The tape prints the label when there is one —
   *  a bank-transfer sale printing "Cash" on the customer's copy is wrong. */
  payment: {
    method: string;
    methodLabel?: string | null;
    amountTendered: string;
    changeDue: string;
  } | null;
  /**
   * Activation codes minted by this sale. `pin` is OPTIONAL because not every
   * caller may see one: the codes are plaintext by design for gift-card UX and
   * the customer's own tape prints them, but the admin drill-down is a
   * back-office audit view and gets the PIN withheld. A missing pin prints as
   * masked rather than blank, so the row still shows a code was issued.
   */
  kitCodes: Array<{ code: string; pin?: string | null }>;
  header: ReceiptHeader | null;
  /** Charges added on top of the goods, each printed under its own label.
   *  `customFeeAmount` is the TOTAL and `customFees` the breakdown; an order
   *  placed before the breakdown existed has only the first two, which
   *  `orderCustomFeeLines` synthesises a single line from. */
  customFeeLabel?: string | null;
  customFeeAmount?: string | null;
  customFees?: Array<{ code: string | null; label: string; amount: string }> | null;
  /** Customer the sale was rung up for. */
  memberName?: string | null;
  /** Till operator who processed it. */
  cashierName?: string | null;
  /** Branch / stockist name. Distinct from the order-code prefix. */
  branchName?: string | null;
  /** ISO timestamp of the sale, printed as separate date + time rows. */
  createdAt?: string | null;
  /** POS remarks. Off by default — internal memo, not customer-facing. */
  remarks?: string | null;
  /**
   * Per-branch visibility flags (`branches.receipt_fields`), sparse. Undefined
   * or missing keys resolve to the shared defaults, so an unconfigured branch
   * prints everything rather than a blank tape.
   */
  fields?: Partial<ReceiptFieldFlags>;
};

/**
 * The order shape `orderToReceipt` needs. Deliberately STRUCTURAL rather than
 * the cashier's `PosOrderDetail` or the API's row type — this package must not
 * learn what a POS order is in full just to format a tape.
 */
export type ReceiptSourceOrder = {
  code: string;
  status: string;
  items: PosReceiptItem[];
  subtotal: string;
  discountTotal: string;
  total: string;
  payments: Array<{
    method: string;
    methodLabel?: string | null;
    amountTendered: string;
    changeDue: string;
  }>;
  kitCodes: Array<{ code: string; pin?: string | null }>;
  branch: {
    name: string;
    receiptHeader?: ReceiptHeader | null;
    receiptFields?: Partial<ReceiptFieldFlags> | null;
  } | null;
  customFeeLabel?: string | null;
  customFeeAmount?: string | null;
  customFees?: Array<{ code: string | null; label: string; amount: string }> | null;
  buyerName?: string | null;
  cashierName?: string | null;
  createdAt: string;
  notes?: string | null;
};

/**
 * The ONE mapping from a fetched order to the printable tape.
 *
 * Shared by the releasing queue, the cashier's Transaction History reprint and
 * the admin sales drill-down so they can never drift. That drift is not
 * hypothetical: the tape once printed totals with no line items because a call
 * site built its own object and omitted `items` (fixed in 9ecef44). A reprint
 * that disagrees with the original tape is worse than no reprint at all — the
 * whole point is that a customer's copy and the shop's copy match.
 *
 * The checkout flow in pos.tsx deliberately does NOT use this: it renders
 * straight from the checkout response, which carries change-due and tendered
 * figures that only exist at the moment of sale.
 */
export function orderToReceipt(o: ReceiptSourceOrder): ReceiptData {
  const payment = o.payments[0] ?? null;
  return {
    orderCode: o.code,
    status: o.status,
    items: o.items,
    subtotal: o.subtotal,
    discountTotal: o.discountTotal,
    total: o.total,
    payment: payment
      ? {
          method: payment.method,
          methodLabel: payment.methodLabel,
          amountTendered: payment.amountTendered,
          changeDue: payment.changeDue,
        }
      : null,
    kitCodes: o.kitCodes.map((c) => ({ code: c.code, pin: c.pin })),
    header: o.branch?.receiptHeader ?? null,
    customFeeLabel: o.customFeeLabel,
    customFeeAmount: o.customFeeAmount,
    customFees: o.customFees,
    memberName: o.buyerName,
    cashierName: o.cashierName,
    branchName: o.branch?.name ?? null,
    createdAt: o.createdAt,
    remarks: o.notes,
    fields: o.branch?.receiptFields ?? undefined,
  };
}
